<template>
  <div
    v-if="botAttacks.byCountry?.length"
    class="executive-summary-bot-attacks pa-6 mt-10 page-break-avoid"
    data-testid="executive-summary-bot-attacks-card"
  >
    <div class="d-flex align-center ga-10">
      <div class="executive-summary-bot-attacks__chart">
        <doughnut-chart :data="chartData" :options="chartOptions" />
      </div>
      <div class="flex-grow-1">
        <div class="subtitle1 mb-4">
          {{ $t("reports.executiveSummaryReport.suspectedBotAttacks") }}
        </div>
        <div class="executive-summary-bot-attacks__legend" :style="legendStyle">
          <div
            v-for="entry in legend"
            :key="entry.country"
            class="d-flex align-center ga-2 executive-summary-bot-attacks__row"
            data-testid="executive-summary-bot-attacks-legend-row"
          >
            <span
              class="executive-summary-bot-attacks__dot"
              :style="{ backgroundColor: entry.color }"
            ></span>
            <span class="body2 executive-summary-bot-attacks__country">
              {{ entry.country }}
            </span>
            <span class="body3 text-indigo-medium">({{ entry.share }}%)</span>
            <v-spacer />
            <span class="subtitle2">{{ formatReportNumber(entry.count) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { useTheme } from "vuetify";
import DoughnutChart from "@/components/DoughnutChart.vue";
import { formatReportNumber, formatReportPercentValue } from "@/_helpers/reports";
import { REPORT_SLICE_COLOR_ORDER } from "@/constants/reports";
import type { ExecutiveSummaryBotAttacks } from "@/_store/reports.module";

// The theme's categorical "label" family, not DoughnutChartColors: the latter carries
// SUCCESS green and ERROR red, which read as pass/fail semantics on a country breakdown.
// The legend is capped at six rows below, so the palette is never cycled and no two slices
// can share a colour.

const LEGEND_COLUMNS = 2;

// The mock shows the five largest countries and folds everything else into one "Other" row.
const LEGEND_TOP_COUNTRIES = 5;

// The backend already folds its own tail into a row named "Other". That row is an aggregate, not
// a country, so it must never compete for a top-five slot: in the `full` fixture its 10 is the
// largest of the six values, and ranking it would both push a real country out AND leave the
// derived remainder to render a second "Other" beside it, double-counting the tail.
//
// Matching by label is a coupling, and the honest fix is a discriminator on the payload
// (`isOther`) — until then it is compared case-insensitively and trimmed so that "OTHER" and
// " Other" are caught too. Dropping the match is NOT an option: the remainder being derived from
// `total` is what makes the row's VALUE right, not what keeps the aggregate out of the ranking.
const BACKEND_OTHER_LABEL = "other";
const isBackendAggregate = (country: string): boolean =>
  country.trim().toLowerCase() === BACKEND_OTHER_LABEL;

export default defineComponent({
  components: { DoughnutChart },
  props: {
    botAttacks: {
      type: Object as PropType<ExecutiveSummaryBotAttacks>,
      required: true,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const theme = useTheme();

    const sliceColors = computed(() =>
      REPORT_SLICE_COLOR_ORDER.map((token) => theme.current.value.colors[token])
    );

    const legend = computed(() => {
      const top = (props.botAttacks.byCountry ?? [])
        .filter(({ country }) => !isBackendAggregate(country))
        .sort((a, b) => b.count - a.count)
        .slice(0, LEGEND_TOP_COUNTRIES);

      // Derived from `total` rather than by summing the tail, so the row also absorbs the
      // backend's own aggregate and stays correct even if `total` counts anything not itemised.
      const otherCount = props.botAttacks.total - top.reduce((sum, { count }) => sum + count, 0);

      // Pinned last whatever its share: in the mock "Other" is the largest of the six rows and
      // still sits at the end, so it is appended after ranking rather than ranked with the rest.
      const rows =
        otherCount > 0
          ? [
              ...top,
              {
                country: i18n.t("reports.executiveSummaryReport.otherCountries"),
                count: otherCount,
              },
            ]
          : top;

      return rows.map(({ country, count }, index) => ({
        country,
        count,
        color: sliceColors.value[index % sliceColors.value.length],
        share:
          props.botAttacks.total > 0
            ? formatReportPercentValue((count / props.botAttacks.total) * 100)
            : formatReportPercentValue(0),
      }));
    });

    const chartData = computed(() => ({
      labels: legend.value.map(({ country }) => country),
      datasets: [
        {
          data: legend.value.map(({ count }) => count),
          backgroundColor: legend.value.map(({ color }) => color),
          cutout: "80%",
        },
      ],
    }));

    const chartOptions = computed(() => ({
      insideTextData: {
        title: formatReportNumber(props.botAttacks.total),
        subtitle: i18n.t("reports.executiveSummaryReport.botAttacks"),
      },
    }));

    // The mock fills the legend column-first, so the row count has to be explicit —
    // `grid-auto-flow: column` alone would keep adding columns instead of wrapping.
    const legendStyle = computed(() => ({
      gridTemplateRows: `repeat(${Math.ceil(legend.value.length / LEGEND_COLUMNS)}, auto)`,
    }));

    return { legend, legendStyle, chartData, chartOptions, formatReportNumber };
  },
});
</script>

<style scoped lang="scss">
.executive-summary-bot-attacks {
  border: 1px solid rgb(var(--v-theme-indigo-faint));
  border-radius: 8px;

  &__chart {
    width: 250px;
    height: 250px;
    flex: 0 0 auto;
  }

  &__legend {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 8px 40px;
  }

  // Without min-width: 0 a legend row cannot shrink below its content, so on a narrow
  // column the count on the right is clipped by the card instead of the name giving way.
  &__row {
    min-width: 0;
  }

  &__country {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex: 0 0 auto;
  }
}
</style>
